/* eslint-disable no-underscore-dangle */

import canvasText from 'canvas-txt';
import { Property } from 'csstype';
import { assignSafe } from '../CodesThatsGottaGoSomewhereElse';
import { Matrix } from './geom/Matrix';

type Base =
    CanvasCompositing &
    CanvasDrawImage &
    CanvasDrawPath &
    CanvasFillStrokeStyles &
    CanvasFilters &
    CanvasImageData &
    CanvasImageSmoothing &
    CanvasPath &
    CanvasPathDrawingStyles &
    CanvasRect &
    CanvasShadowStyles &
    // CanvasState &
    CanvasText &
    CanvasTextDrawingStyles &
    CanvasTransform
    ;

export class FontStyle {

    public static default = FontStyle.create({});

    public static create(values: Partial<FontStyle>): FontStyle {
        const font = new FontStyle();
        assignSafe(font, values);
        return font;
    }

    public readonly family: Property.FontFamily = 'Rubik';
    public readonly size: number = 14;
    public readonly style: Property.FontStyle = 'normal';
    public readonly variant: Property.FontVariant = 'normal';
    public readonly weight: Property.FontWeight = 'normal';

    private constructor() {
    }

    public toString() {
        return [this.style, this.variant, this.weight, this.size, `"${this.family}"`].join(' ');
    }
}

export type ShadowStyle = [number, number, number, string];
export const ShadowStyle = Object.assign(
    (x: number, y: number, blur: number, color: string) => [x, y, blur, color] as ShadowStyle,
    {
        default: [0, 0, 0, null] as ShadowStyle,
    }
);

const extensions = {
    reset(clear?: boolean) {
        this.globalAlpha = 1;
        this.globalCompositeOperation = 'source-over';
        this.fillStyle = null;
        this.shadowStyle = null;
        this.strokeStyle = null;
        this.setLineDash([]);
        this.setTransform(Matrix.identity);
        if (clear) {
            this.clearRect(0, 0, this.width, this.height);
        }
    },
    strokeCircle(this: any, cx: number, cy: number, radius: number) {
        this.beginPath();
        this.arc(cx, cy, radius, 0, 2 * Math.PI, false);
        this.stroke();
    },
    fillCircle(this: any, cx: number, cy: number, radius: number) {
        this.beginPath();
        this.arc(cx, cy, radius, 0, 2 * Math.PI, false);
        this.fill();
    },
    strokeLine(this: any, x1: number | number[], y1?: number, x2?: number, y2?: number) {
        if (Array.isArray(x1)) {
            y2 = x1[3];
            x2 = x1[2];
            y1 = x1[1];
            x1 = x1[0];
        }
        this.beginPath();
        this.moveTo(x1, y1);
        this.lineTo(x2, y2);
        this.stroke();
    },
    roundRect(this: any, x: number, y: number, w: number, h: number, r: number) {
        if (w < 2 * r) r = w / 2;
        if (h < 2 * r) r = h / 2;
        r = Math.max(0, r);
        this.beginPath();
        this.moveTo(x + r, y);
        this.arcTo(x + w, y, x + w, y + h, r);
        this.arcTo(x + w, y + h, x, y + h, r);
        this.arcTo(x, y + h, x, y, r);
        this.arcTo(x, y, x + w, y, r);
        this.closePath();
    },
    strokeRoundRect(this: any, x: number, y: number, w: number, h: number, r: number) {
        this.roundRect(x, y, w, h, r);
        this.stroke();
    },
    fillRoundRect(this: any, x: number, y: number, w: number, h: number, r: number) {
        this.roundRect(x, y, w, h, r);
        this.fill();
    },
    // eslint-disable-next-line max-len
    fillTextRect(this: any, text: string, x: number, y: number, w: number, h: number, align = 'left', vAlign = 'top', lineHeight?: number) {
        const fs = this.fontStyle as FontStyle;
        canvasText.font = fs.family;
        canvasText.fontSize = fs.size;
        canvasText.fontStyle = fs.style;
        canvasText.fontVariant = fs.variant;
        canvasText.fontWeight = fs.weight;
        canvasText.align = align;
        canvasText.vAlign = vAlign;
        canvasText.lineHeight = lineHeight ?? 12;
        // canvasText.debug = true; // shows debug info
        canvasText.drawText(this, text, x, y, w, h);
    }
};

const extend = (gfx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {

    // Apply extensions
    Object.assign(gfx, extensions);

    // Apply width/height properties
    {
        Object.defineProperty(gfx, 'width', {
            configurable: false,
            enumerable: true,
            get: () => canvas.width,
        });
        Object.defineProperty(gfx, 'height', {
            configurable: false,
            enumerable: true,
            get: () => canvas.height,
        });
    }

    // Apply fontStyle property
    {
        Object.defineProperty(gfx, '__fontStyle', {
            configurable: false,
            enumerable: false,
            value: FontStyle.default,
            writable: true,
        });
        Object.defineProperty(gfx, 'fontStyle', {
            configurable: false,
            enumerable: true,
            get() {
                return this.__fontStyle;
            },
            set(value: FontStyle) {
                this.__fontStyle = value;
                this.font = value.toString();
            },
        });
    }

    // Apply shadowStyle property
    {
        Object.defineProperty(gfx, '__shadowStyle', {
            configurable: false,
            enumerable: false,
            value: ShadowStyle.default,
            writable: true,
        });
        Object.defineProperty(gfx, 'shadowStyle', {
            configurable: false,
            enumerable: true,
            get() {
                return this.__shadowStyle;
            },
            set(value: ShadowStyle) {
                value = value ?? ShadowStyle.default;
                this.__shadowStyle = value;
                this.shadowOffsetX = value[0];
                this.shadowOffsetY = value[1];
                this.shadowBlur = value[2];
                this.shadowColor = value[3];
            },
        });
    }

    // Mark extended
    Object.defineProperty(gfx, '__extended', {
        configurable: false,
        enumerable: false,
        value: true,
        writable: false,
    });
};

type Extensions = typeof extensions & {
    width: number,
    height: number,
    fontStyle: FontStyle,
    shadowStyle: ShadowStyle,
};

type Omittions = 'font' | 'shadowOffsetX' | 'shadowOffsetY' | 'shadowBlur' | 'shadowColor';

export type UniversalSurfaceRenderer = Omit<Base, Omittions> & Extensions;

export const UniversalSurfaceRenderer = {
    from(canvas: HTMLCanvasElement) {
        const gfx = canvas.getContext('2d') as any;
        if (!gfx.__extended) extend(gfx, canvas);
        // Done
        return gfx as UniversalSurfaceRenderer;
    }
};

